import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';
import { compose, mapProps, withProps } from 'recompose';

import { withPLPSelectPlanHandler } from 'ggApp/modules/rentalPlan';
import {
    currentProductByIdRentalPlans,
    selectedRentalPlanSelector,
} from 'ggApp/modules/rentalPlan/selectors';
import { storeActive } from 'ggApp/modules/store/selectors';
import {
    firstRequest,
    checkoutLandingTracking as checkoutLandingTrackingSelector,
} from 'ggApp/modules/tracking/selectors';
import { locationQuery as locationQuerySelector } from 'ggApp/modules/router/selectors';
import ggTracking from 'ggApp/modules/tracking';
import {
    currentProductById,
    isDronesCategory,
    accessoriesNames as accessoriesNamesSelector,
    imageList as imageListSelector,
    currentVariantTechSpecs,
    videoWidget as videoWidgetSelector,
    announcementDataForCurrentProduct as announcementDataForCurrentProductSelector,
} from 'ggApp/modules/product/selectors';
import { selectProductVariant } from 'ggApp/modules/product/actions';
import ggModal from 'ggApp/modules/modal';
import { registerProductDetailedImpression as registerProductDetailedImpressionActionCreator } from 'ggApp/modules/gaAnalytics';
import { keys as translationKeysSelector } from 'ggApp/modules/translator/selectors';
import {
    CfProductDetailPage,
    contentfulPage as contentfulPageSelector,
    CfProductDetailPageForDrones,
    getDiscountBannerContent,
    CfDiscountBanner,
    getCFDiscountBanner as getCFDiscountBannerAction,
    getCFPartnerDiscountBanner as getCFPartnerDiscountBannerAction,
    CfPartnerProductPage,
} from 'ggApp/modules/contentful';
import { registerProductDetailedImpressionV2 } from 'ggApp/modules/gaAnalytics/actions';
import {
    newCheckoutEnabledSelector,
    discountBannerFeatureFlagData as discountBannerFeatureFlagDataSelector,
    purchaseOptionExtendedFaqEnabled,
    plpBundlesFlag as plpBundlesFlagSelector,
} from 'ggApp/modules/featureFlag/selectors';
import {
    pushParams,
    removeParams,
    redirectus as redirectusAction,
} from 'ggApp/modules/router/actions';
import {
    isIncompletePrivateAccount as isIncompletePrivateAccountSelector,
    isIncompleteBusinessAccount as isIncompleteBusinessAccountSelector,
    isLegacyBusinessAccount as isLegacyBusinessAccountSelector,
    authFlow as authFlowSelector,
    isLoggedIn as isLoggedInSelector,
} from 'ggApp/modules/auth/selectors';

import PartnerProductPage from './PartnerProductPage';

const getContentfulPDPType = (store) =>
    isDronesCategory(store) ? CfProductDetailPageForDrones : CfProductDetailPage;

const filterFaqs = ({ contentfulPLPData, purchaseOptionExtendedFaq, ...props }) => {
    if (contentfulPLPData && !purchaseOptionExtendedFaq) {
        const { faqsList, ...rest } = contentfulPLPData;
        return {
            contentfulPLPData: {
                faqsList: faqsList.filter((item) =>
                    ['kaufen', 'buy', 'comprar', 'kopen'].every(
                        (excludePattern) => !item.title.includes(excludePattern),
                    ),
                ),
                ...rest,
            },
            ...props,
        };
    }
    return { contentfulPLPData, ...props };
};

const enhance = compose(
    connect(
        createStructuredSelector({
            product: currentProductById,
            activeStore: storeActive,
            locationQuery: locationQuerySelector,
            firstRequestEventData: firstRequest,
            rentalPlans: currentProductByIdRentalPlans,
            selectedRentalPlan: selectedRentalPlanSelector,
            checkoutLandingTracking: checkoutLandingTrackingSelector,
            isLoggedIn: isLoggedInSelector,
            translationKeys: translationKeysSelector,
            pdpMarketingMaterials: contentfulPageSelector(getContentfulPDPType),
            purchaseOptionExtendedFaq: purchaseOptionExtendedFaqEnabled,
            contentfulPLPData: contentfulPageSelector(CfPartnerProductPage),
            accessoriesNames: accessoriesNamesSelector,
            imageList: imageListSelector,
            discountBanner: contentfulPageSelector(CfDiscountBanner),
            discountBannerFeatureFlagData: discountBannerFeatureFlagDataSelector,
            techSpecs: currentVariantTechSpecs,
            videoWidget: videoWidgetSelector,
            isIncompletePrivateAccount: isIncompletePrivateAccountSelector,
            isIncompleteBusinessAccount: isIncompleteBusinessAccountSelector,
            isLegacyBusinessAccount: isLegacyBusinessAccountSelector,
            initialAuthFlow: authFlowSelector,
            newCheckoutEnabled: newCheckoutEnabledSelector,
            plpBundlesFlag: plpBundlesFlagSelector,
            productAnnouncementData: announcementDataForCurrentProductSelector,
        }),
        (dispatch) => ({
            trackingActions: bindActionCreators(ggTracking.actions, dispatch),
            registerProductDetailedImpression: bindActionCreators(
                registerProductDetailedImpressionActionCreator,
                dispatch,
            ),
            registerProductDetailedImpressionV2: bindActionCreators(
                registerProductDetailedImpressionV2,
                dispatch,
            ),
            selectProductVariant: bindActionCreators(selectProductVariant, dispatch),
            modalActions: bindActionCreators(ggModal.actions, dispatch),
            getCFDiscountBanner: bindActionCreators(getCFDiscountBannerAction, dispatch),
            getCFPartnerDiscountBanner: bindActionCreators(
                getCFPartnerDiscountBannerAction,
                dispatch,
            ),
            pushUrlParams: bindActionCreators(pushParams, dispatch),
            removeUrlParams: bindActionCreators(removeParams, dispatch),
            redirectus: bindActionCreators(redirectusAction, dispatch),
        }),
    ),
    mapProps(filterFaqs),
    withProps(({ activeStore, product, discountBanner = {}, discountBannerFeatureFlagData }) => {
        const flagValue = discountBannerFeatureFlagData && discountBannerFeatureFlagData.variantKey;
        const { feedbackBannerText, feedbackBannerCopyableText } = getDiscountBannerContent({
            flagValue,
            storeCode: activeStore.code,
            productBrand: product.brand,
            discountBanner,
        });
        const feedbackBannerAvailable = flagValue && flagValue !== 'disabled';
        return {
            feedbackBannerAvailable,
            feedbackBannerText,
            feedbackBannerCopyableText,
        };
    }),
    withProps(({ rentalPlans, videoWidget }) => ({
        isFlexibleProduct: rentalPlans.length === 1 && rentalPlans[0].minimumMonths === 1,
        isValidIdForVideo: videoWidget && videoWidget.id && /^[0-9]*$/.test(`${videoWidget.id}`),
    })),
    withPLPSelectPlanHandler,
);

export default enhance(PartnerProductPage);
